import React, { useState, useEffect } from 'react';
import { X, Upload, Star, Wrench, Hammer, Cpu, Printer, Plus } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Switch from '../../../components/ui/Switch';
import { sellerProductService } from '../../../services/sellerProductService';
import { imageUploadService } from '../../../utils/imageUploadService';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { validateVIN } from '../../../utils/validators';
import { manufacturingService } from '../../../services/manufacturingService';

// Define seller product statuses
const SELLER_PRODUCT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'deleted', label: 'Deleted' },
];

export default function SellerProductModal({ product, onClose }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [allMaterials, setAllMaterials] = useState([]);
  const [mfgAccounts, setMfgAccounts] = useState([]);
  const [globalMfgOpt, setGlobalMfgOpt] = useState(null);
  const [partnerConfig, setPartnerConfig] = useState(null);
  const [customColorsRegistry, setCustomColorsRegistry] = useState({}); // { [matName]: [] }
  const [customColorName, setCustomColorName] = useState({}); // { [matName]: '' }
  const [customColorRgb, setCustomColorRgb] = useState({}); // { [matName]: '' }
  const [customColorMetallicChecked, setCustomColorMetallicChecked] = useState({}); // { [matName]: false }
  const [customColorMetallicValue, setCustomColorMetallicValue] = useState({}); // { [matName]: '1.0' }

  const deleteCustomColor = (fieldName, matName, color) => {
    // 1. Remove from customColorsRegistry
    setCustomColorsRegistry(prev => {
      const list = prev[matName] || [];
      return {
        ...prev,
        [matName]: list.filter(c => c !== color)
      };
    });
    
    // 2. Remove from formData active selection
    setFormData(prev => {
      const colorMap = prev[fieldName] || {};
      const list = colorMap[matName] || [];
      const updated = list.filter(c => c !== color);
      return {
        ...prev,
        [fieldName]: {
          ...colorMap,
          [matName]: updated
        }
      };
    });
  };

  const parseColor = (colorStr) => {
    try {
      if (colorStr && typeof colorStr === 'string' && colorStr.startsWith('{') && colorStr.endsWith('}')) {
        return JSON.parse(colorStr);
      }
    } catch (e) {
      console.error('Failed to parse color JSON:', colorStr);
    }
    return { name: colorStr || 'Unnamed Color', rgb: '', isMetallic: false, isPolished: false };
  };

  const [formData, setFormData] = useState({
    price: '',
    stockQuantity: '',
    purchaseLimit: '',
    status: 'active',
    condition: 'New',
    description: '',
    stockCode: '',
    sellerImageUrls: [],
    isCertified: false,
    staffpick: false,
    isSellingPhysicalProduct: false,
    vinSn: '',
    isSelfManufacturing: true,
    manufacturerId: '',
    sellProductOnly: false, // NEW: Sell product assembly only flag
    is3dManufacturing: false,
    isCncCapable: false,
    mfg3dTypes: [],
    mfg3dMaterials: [],
    mfg3dColors: {},
    mfgCncTypes: [],
    mfgCncMaterials: [],
    mfgCncColors: {},
    keywords: '',
    seasonalSetting: '',
    holidaySetting: '',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [vinError, setVinError] = useState('');
  const [isPre1981, setIsPre1981] = useState(false);
  const [isPre1969, setIsPre1969] = useState(false);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Load global master manufacturing configurations
  useEffect(() => {
    const loadGlobalOptions = async () => {
      const gProductId = product?.productId;
      if (gProductId) {
        const { data: prodData } = await supabase
          .from('products')
          .select('is_3d_manufacturing, is_cnc_capable, sell_product_only, mfg_3d_types, mfg_3d_materials, mfg_3d_colors, mfg_cnc_types, mfg_cnc_materials, mfg_cnc_colors')
          .eq('id', gProductId)
          .maybeSingle();
        
        if (prodData) {
          setGlobalMfgOpt({
            offered_methods: prodData.is_3d_manufacturing && prodData.is_cnc_capable ? 'both' : (prodData.is_3d_manufacturing ? '3d_only' : (prodData.is_cnc_capable ? 'cnc_only' : 'none')),
            mfg_3d_types: prodData.mfg_3d_types,
            mfg_3d_materials: prodData.mfg_3d_materials,
            mfg_3d_colors: prodData.mfg_3d_colors,
            mfg_cnc_types: prodData.mfg_cnc_types,
            mfg_cnc_materials: prodData.mfg_cnc_materials,
            mfg_cnc_colors: prodData.mfg_cnc_colors
          });
        }
      }
    };
    loadGlobalOptions();
  }, [product]);

  // Load selected partner configurations
  useEffect(() => {
    const loadPartnerConfig = async () => {
      const partnerId = formData.manufacturerId;
      const selectedMfg = mfgAccounts.find(m => m.id === partnerId);
      const partnerAccountId = selectedMfg?.manufacturer_account_id;

      if (partnerAccountId && !formData.isSelfManufacturing) {
        const { data } = await supabase
          .from('manufacturing_supplier_configs')
          .select('*')
          .eq('manufacturer_id', partnerAccountId)
          .maybeSingle();
        setPartnerConfig(data);
      } else {
        setPartnerConfig(null);
      }
    };
    loadPartnerConfig();
  }, [formData.manufacturerId, formData.isSelfManufacturing, mfgAccounts]);

  const allowed3dTypes = React.useMemo(() => {
    if (globalMfgOpt && globalMfgOpt.offered_methods !== '3d_only' && globalMfgOpt.offered_methods !== 'both') {
      return [];
    }
    const masterTypes = globalMfgOpt && globalMfgOpt.mfg_3d_types && globalMfgOpt.mfg_3d_types.length > 0
      ? globalMfgOpt.mfg_3d_types
      : [ 'Resin Printing (SLA/DLP/MSLA)', 'Powder Printing (SLS/MJF)', 'Filament Printing (FDM/FFF)', 'Material Jetting (PolyJet)', 'Binder Jetting' ];
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterTypes;
    }
    const partnerTypes = partnerConfig.capabilities?.machines || [];
    if (partnerTypes.length === 0) return masterTypes;
    return masterTypes.filter(type => partnerTypes.includes(type));
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowed3dMaterials = React.useMemo(() => {
    const masterMaterials = globalMfgOpt && globalMfgOpt.mfg_3d_materials && globalMfgOpt.mfg_3d_materials.length > 0
      ? globalMfgOpt.mfg_3d_materials
      : allMaterials;
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterMaterials;
    }
    const partnerMaterials = partnerConfig.capabilities?.materials || [];
    if (partnerMaterials.length === 0) return masterMaterials;
    return masterMaterials.filter(mat => {
      const matName = typeof mat === 'string' ? mat : mat.name;
      return partnerMaterials.includes(matName);
    });
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing, allMaterials]);

  const allowed3dColors = React.useMemo(() => {
    // Build a fallback map of all default colors from the materials catalog
    const defaultColorsMap = {};
    allMaterials.forEach(m => {
      const matName = typeof m === 'string' ? m : m.name;
      defaultColorsMap[matName] = typeof m === 'string' ? [] : (m.colors || []);
    });

    const masterColors = globalMfgOpt?.mfg_3d_colors && Object.keys(globalMfgOpt.mfg_3d_colors).length > 0
      ? globalMfgOpt.mfg_3d_colors
      : defaultColorsMap;

    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterColors;
    }
    const partnerColors = partnerConfig.capabilities?.colors || {};
    const intersection = {};
    const mats = globalMfgOpt && globalMfgOpt.mfg_3d_materials && globalMfgOpt.mfg_3d_materials.length > 0
      ? globalMfgOpt.mfg_3d_materials
      : allMaterials;
    mats.forEach(m => {
      const matName = typeof m === 'string' ? m : m.name;
      const mColors = masterColors[matName] || (typeof m === 'string' ? [] : m.colors || []);
      const pColors = partnerColors[matName] || [];
      if (pColors.length === 0) {
        intersection[matName] = mColors;
      } else {
        intersection[matName] = mColors.filter(color => pColors.includes(color));
      }
    });
    return intersection;
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing, allMaterials]);

  const allowedCncTypes = React.useMemo(() => {
    if (globalMfgOpt && globalMfgOpt.offered_methods !== 'cnc_only' && globalMfgOpt.offered_methods !== 'both') {
      return [];
    }
    const masterTypes = globalMfgOpt && globalMfgOpt.mfg_cnc_types && globalMfgOpt.mfg_cnc_types.length > 0
      ? globalMfgOpt.mfg_cnc_types
      : ['3-Axis Milling', '5-Axis Milling', 'Turning / Lathe', 'EDM Wire', 'Laser Cutting', 'Waterjet'];
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterTypes;
    }
    const partnerTypes = partnerConfig.capabilities?.machines || [];
    if (partnerTypes.length === 0) return masterTypes;
    return masterTypes.filter(type => partnerTypes.includes(type));
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowedCncMaterials = React.useMemo(() => {
    const masterMaterials = globalMfgOpt && globalMfgOpt.mfg_cnc_materials && globalMfgOpt.mfg_cnc_materials.length > 0
      ? globalMfgOpt.mfg_cnc_materials
      : allMaterials;
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterMaterials;
    }
    const partnerMaterials = partnerConfig.capabilities?.materials || [];
    if (partnerMaterials.length === 0) return masterMaterials;
    return masterMaterials.filter(mat => {
      const matName = typeof mat === 'string' ? mat : mat.name;
      return partnerMaterials.includes(matName);
    });
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing, allMaterials]);

  const allowedCncColors = React.useMemo(() => {
    // Build a fallback map of all default colors from the materials catalog
    const defaultColorsMap = {};
    allMaterials.forEach(m => {
      const matName = typeof m === 'string' ? m : m.name;
      defaultColorsMap[matName] = typeof m === 'string' ? [] : (m.colors || []);
    });

    const masterColors = globalMfgOpt?.mfg_cnc_colors && Object.keys(globalMfgOpt.mfg_cnc_colors).length > 0
      ? globalMfgOpt.mfg_cnc_colors
      : defaultColorsMap;

    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterColors;
    }
    const partnerColors = partnerConfig.capabilities?.colors || {};
    const intersection = {};
    const mats = globalMfgOpt && globalMfgOpt.mfg_cnc_materials && globalMfgOpt.mfg_cnc_materials.length > 0
      ? globalMfgOpt.mfg_cnc_materials
      : allMaterials;
    mats.forEach(m => {
      const matName = typeof m === 'string' ? m : m.name;
      const mColors = masterColors[matName] || (typeof m === 'string' ? [] : m.colors || []);
      const pColors = partnerColors[matName] || [];
      if (pColors.length === 0) {
        intersection[matName] = mColors;
      } else {
        intersection[matName] = mColors.filter(color => pColors.includes(color));
      }
    });
    return intersection;
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing, allMaterials]);
  const [sellerTier, setSellerTier] = useState('entry');

  useEffect(() => {
    const loadMfgData = async () => {
      try {
        const [mats, mfgRes] = await Promise.all([
          manufacturingService.getMaterials(true),
          supabase
            .from('manufacturers')
            .select('id, name, manufacturer_account_id')
            .not('manufacturer_account_id', 'is', null)
        ]);
        setAllMaterials(mats || []);
        setMfgAccounts(mfgRes.data || []);

        const targetUserId = user?.id;
        const targetSellerId = product?.sellerId;
        if (targetUserId || targetSellerId) {
          let query = supabase.from('seller_accounts').select('tier');
          if (targetSellerId) {
            query = query.eq('id', targetSellerId);
          } else {
            query = query.eq('user_id', targetUserId);
          }
          const { data: sellerAcc } = await query.maybeSingle();
          if (sellerAcc) {
            setSellerTier(sellerAcc.tier || 'entry');
          }
        }
      } catch (err) {
        console.error('Failed to load standardized manufacturing parameters:', err);
      }
    };
    loadMfgData();
  }, [user?.id, product?.sellerId]);

  useEffect(() => {
    if (product) {
      const hasPrice = parseFloat(product.price || 0) > 0;
      setFormData({
        price: product.price || '',
        stockQuantity: product.stockQuantity || product.stockAmount || '',
        purchaseLimit: product.purchaseLimit || '',
        status: product.status || 'active',
        condition: product.condition || 'New',
        description: product.description || '',
        stockCode: product.stockCode || '',
        sellerImageUrls: product.sellerImageUrls || [],
        isCertified: product.isCertified || false,
        staffpick: product.staffpick || false,
        isSellingPhysicalProduct: hasPrice,
        vinSn: product.vinSn || '',
        specs: product.specs ? JSON.stringify(product.specs, null, 2) : '{}',
        isSelfManufacturing: typeof product.isSelfManufacturing === 'boolean' ? product.isSelfManufacturing : true,
        manufacturerId: product.manufacturerId || '',
        sellProductOnly: product.sellProductOnly || product.sell_product_only || false,
        is3dManufacturing: false,
        isCncCapable: false,
        mfg3dTypes: [],
        mfg3dMaterials: [],
        mfg3dColors: {},
        mfgCncTypes: [],
        mfgCncMaterials: [],
        mfgCncColors: {},
        keywords: product.keywords || '',
        seasonalSetting: product.seasonalSetting || product.seasonal_setting || '',
        holidaySetting: product.holidaySetting || product.holiday_theme || product.holiday_setting || '',
      });

      // Load normalized manufacturing options from the database
      supabase
        .from('manufacturing_options')
        .select('*')
        .eq('seller_product_id', product.id)
        .maybeSingle()
        .then(({ data: mfgOpt }) => {
          if (mfgOpt) {
            setFormData(prev => ({
              ...prev,
              isSelfManufacturing: product.isSelfManufacturing ?? mfgOpt.is_self_manufacturing ?? true,
              manufacturerId: product.manufacturerId || mfgOpt.manufacturer_id || '',
              is3dManufacturing: mfgOpt.offered_methods === '3d_only' || mfgOpt.offered_methods === 'both',
              isCncCapable: mfgOpt.offered_methods === 'cnc_only' || mfgOpt.offered_methods === 'both',
              mfg3dTypes: mfgOpt.mfg_3d_types || [],
              mfg3dMaterials: mfgOpt.mfg_3d_materials || [],
              mfg3dColors: mfgOpt.mfg_3d_colors || {},
              mfgCncTypes: mfgOpt.mfg_cnc_types || [],
              mfgCncMaterials: mfgOpt.mfg_cnc_materials || [],
              mfgCncColors: mfgOpt.mfg_cnc_colors || {},
            }));

            // Symmetrically populate customColorsRegistry with existing custom colors on load
            const customColors = {};
            const m3d = mfgOpt.mfg_3d_colors || {};
            const mcnc = mfgOpt.mfg_cnc_colors || {};
            
            const savedRegistry = m3d['__customRegistry'] || mcnc['__customRegistry'] || {};
            
            Object.keys(m3d).forEach(mat => {
              if (mat !== '__customRegistry') {
                customColors[mat] = m3d[mat] || [];
              }
            });
            Object.keys(mcnc).forEach(mat => {
              if (mat !== '__customRegistry') {
                customColors[mat] = [...(customColors[mat] || []), ...(mcnc[mat] || [])];
              }
            });
            
            // Merge with the saved registry to preserve inactive custom colors!
            Object.keys(savedRegistry).forEach(mat => {
              const list = savedRegistry[mat] || [];
              customColors[mat] = [...new Set([...(customColors[mat] || []), ...list])];
            });
            
            setCustomColorsRegistry(customColors);
          }
        });

      if (product.specs) {
          setIsPre1981(!!product.specs.isPre1981);
          setIsPre1969(!!product.specs.isPre1969);
      }
      // Initialize previews with existing URLs mapped correctly
      setImagePreviews((product.sellerImageUrls || []).filter(url => url && typeof url === 'string').map(url => 
        url.startsWith('http') || url.startsWith('blob:') ? url : imageUploadService.getPublicUrl(url, 'seller-product-images')
      ));
    }
  }, [product]);

  const toggleArrayField = (fieldName, item) => {
    setFormData(prev => {
      const arr = prev[fieldName] || [];
      const updated = arr.includes(item)
        ? arr.filter(i => i !== item)
        : [...arr, item];
      return { ...prev, [fieldName]: updated };
    });
  };

  const toggleColorForMaterial = (fieldName, matName, color) => {
    setFormData(prev => {
      const colorMap = prev[fieldName] || {};
      const currentColors = colorMap[matName] || [];
      const updatedColors = currentColors.includes(color)
        ? currentColors.filter(c => c !== color)
        : [...currentColors, color];
      return {
        ...prev,
        [fieldName]: {
          ...colorMap,
          [matName]: updatedColors
        }
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
    }));
    if (name === 'vinSn') {
      setVinError('');
    }
  };

  const handleVinBlur = (e) => {
    const val = e.target.value;
    const isVehicle = (product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles');
    if (isVehicle && val) {
      if (!validateVIN(val, { isPre1981, isPre1969 })) {
        setVinError('Invalid VIN format. Please check the Vehicle Identification Number.');
      } else {
        setVinError('');
      }
    } else {
      setVinError('');
    }
  };

  useEffect(() => {
    // Revalidate if toggles change and we already have a VIN
    if (formData.vinSn) {
        handleVinBlur({ target: { value: formData.vinSn } });
    }
  }, [isPre1981, isPre1969]);

  const handleStatusChange = (value) => {
    setFormData(prev => ({ ...prev, status: value }));
  };

  const handleImageFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    const newFiles = [];
    const newPreviews = [];

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        setError(`File ${file.name} is not a valid image type (JPG, PNG, WEBP).`);
        return;
      }
      if (file.size > maxSize) {
        setError(`File ${file.name} exceeds the 5MB size limit.`);
        return;
      }
      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    setError('');
    setSelectedImageFiles(prev => [...prev, ...newFiles]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    const newImagePreviews = imagePreviews.filter((_, i) => i !== index);
    setImagePreviews(newImagePreviews);

    // Check if the removed image was an existing URL or a new file
    if (index < formData.sellerImageUrls.length) {
      // It was an existing URL
      const newImageUrls = formData.sellerImageUrls.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, sellerImageUrls: newImageUrls }));
    } else {
      // It was a new file
      const newFileIndex = index - formData.sellerImageUrls.length;
      const newSelectedFiles = selectedImageFiles.filter((_, i) => i !== newFileIndex);
      setSelectedImageFiles(newSelectedFiles);
    }
  };
  
  const makePrimaryImage = (index) => {
    if (index === 0) return; // Already primary

    const newImagePreviews = [...imagePreviews];
    const primaryPreview = newImagePreviews.splice(index, 1)[0];
    newImagePreviews.unshift(primaryPreview);
    setImagePreviews(newImagePreviews);

    const newImageUrls = [...formData.sellerImageUrls];
    // Check if the moved image is an existing one
    if (index < newImageUrls.length) {
        const primaryUrl = newImageUrls.splice(index, 1)[0];
        newImageUrls.unshift(primaryUrl);
    }
    
    const fileIndex = index - newImageUrls.length;
    if (fileIndex >= 0) {
        const newSelectedFiles = [...selectedImageFiles];
        const primaryFile = newSelectedFiles.splice(fileIndex, 1)[0];
        newSelectedFiles.unshift(primaryFile);
        setSelectedImageFiles(newSelectedFiles);
    }
    
    setFormData(prev => ({ ...prev, sellerImageUrls: newImageUrls }));
  };

  const uploadImages = async () => {
    if (selectedImageFiles.length === 0) return formData.sellerImageUrls;

    setUploading(true);
    const uploadPromises = selectedImageFiles.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `seller-product-images/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('seller-product-images')
        .upload(filePath, file);

      if (uploadError) {
        throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
      }

      const { data } = supabase.storage.from('seller-product-images').getPublicUrl(filePath);
      return data.publicUrl;
    });

    try {
      const newUrls = await Promise.all(uploadPromises);
      return [...formData.sellerImageUrls, ...newUrls];
    } catch (error) {
      setError(error.message);
      return formData.sellerImageUrls;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.isSellingPhysicalProduct) {
      const parsedPrice = parseFloat(formData.price || 0);
      if (isNaN(parsedPrice) || parsedPrice <= 0) {
        setError('Price must be greater than 0.00 when "Sell Physical Product" is enabled.');
        setLoading(false);
        return;
      }
    }

    const isVehicle = (product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles');
    if (isVehicle && formData.vinSn) {
        if (!validateVIN(formData.vinSn, { isPre1981, isPre1969 })) {
            setVinError('Invalid VIN format. Please check the Vehicle Identification Number.');
            setLoading(false);
            return;
        }
    }

    let parsedSpecs = {};
    try {
      parsedSpecs = formData.specs && formData.specs.trim() !== '' ? JSON.parse(formData.specs) : {};
    } catch (e) {
      setError('Invalid JSON format for Specifications.');
      setLoading(false);
      return;
    }
    
    if (isPre1981 || isPre1969) {
        parsedSpecs.isPre1981 = isPre1981;
        parsedSpecs.isPre1969 = isPre1969;
    }

    try {
      const updatedImageUrls = await uploadImages();
      
      const submitData = {
        price: formData.isSellingPhysicalProduct ? parseFloat(formData.price || 0) : 0,
        stock_amount: formData.stockQuantity === '' ? 0 : parseInt(formData.stockQuantity, 10),
        purchase_limit: formData.purchaseLimit === '' ? null : parseInt(formData.purchaseLimit, 10),
        status: formData.status,
        condition: formData.condition || 'New',
        description: formData.description,
        stock_code: formData.stockCode,
        seller_image_url: updatedImageUrls,
        staffpick: formData.staffpick,
        vin_sn: (formData.isSellingPhysicalProduct && formData.vinSn && formData.vinSn.trim() !== '') ? formData.vinSn : null,
        specs: parsedSpecs,
        is_self_manufacturing: formData.isSelfManufacturing,
        manufacturer_id: formData.isSelfManufacturing ? null : (formData.manufacturerId || null),
        sell_product_only: Boolean(formData.sellProductOnly), // NEW: Save to seller_products override!
        keywords: formData.keywords || null,
        seasonal_setting: formData.seasonalSetting || null,
        holiday_setting: formData.holidaySetting || null,
      };

      await sellerProductService.update(product.id, submitData);

      // Calculate offered_methods dynamically
      let offeredMethods = 'none';
      if (formData.isSelfManufacturing) {
        if (formData.is3dManufacturing && formData.isCncCapable) {
          offeredMethods = 'both';
        } else if (formData.is3dManufacturing) {
          offeredMethods = '3d_only';
        } else if (formData.isCncCapable) {
          offeredMethods = 'cnc_only';
        }
      }

      // Save to normalized manufacturing_options table
      const mfg3dColorsPayload = { ...(formData.mfg3dColors || {}) };
      mfg3dColorsPayload['__customRegistry'] = customColorsRegistry;
      
      const mfgCncColorsPayload = { ...(formData.mfgCncColors || {}) };
      mfgCncColorsPayload['__customRegistry'] = customColorsRegistry;

      const mfgOptionsPayload = {
        seller_product_id: product.id,
        offered_methods: offeredMethods,
        mfg_3d_types: formData.mfg3dTypes || [],
        mfg_3d_materials: formData.mfg3dMaterials || [],
        mfg_3d_colors: mfg3dColorsPayload,
        mfg_cnc_types: formData.mfgCncTypes || [],
        mfg_cnc_materials: formData.mfgCncMaterials || [],
        mfg_cnc_colors: mfgCncColorsPayload
      };

      const { error: mfgErr } = await supabase
        .from('manufacturing_options')
        .upsert(mfgOptionsPayload, { onConflict: 'seller_product_id' });

      if (mfgErr) throw mfgErr;
      
      onClose(true);
    } catch (err) {
      console.error('❌ Failed to update seller product:', err);
      setError(err?.message || 'Failed to update product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isGlobal3dEnabled = !globalMfgOpt || globalMfgOpt.offered_methods === '3d_only' || globalMfgOpt.offered_methods === 'both';
  const isGlobalCncEnabled = !globalMfgOpt || globalMfgOpt.offered_methods === 'cnc_only' || globalMfgOpt.offered_methods === 'both';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[82vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Seller Product
          </h2>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Headers */}
        <div className="flex border-b border-gray-200 px-6 bg-gray-50">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-3 px-4 font-semibold text-sm border-b-2 transition-colors ${
              activeTab === 'general' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            General Info
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('manufacturing')}
            className={`py-3 px-4 font-semibold text-sm border-b-2 transition-colors ${
              activeTab === 'manufacturing' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Manufacturing
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          {activeTab === 'general' && (
            <>
              <div className="md:col-span-2 p-4 border border-gray-200 rounded-md bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Main Product Details</h3>
                <div className="flex items-center gap-4">
                  <img src={product.thumbnailPath?.startsWith('http') ? product.thumbnailPath : imageUploadService.getPublicUrl(product.thumbnailPath, 'product-thumbnails')} alt={product.name} className="w-24 h-24 object-cover rounded-md" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm flex-1">
                    <div><span className="font-medium text-gray-600">Name:</span> {product.name}</div>
                    <div><span className="font-medium text-gray-600">SKU:</span> {product.sku}</div>
                    <div><span className="font-medium text-gray-600">Category:</span> {product.categoryName}</div>
                    <div><span className="font-medium text-gray-600">Manufacturer:</span> {product.manufacturerName}</div>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 flex items-center justify-between p-4 border border-gray-200 rounded-md bg-blue-50">
                 <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Sell Physical Product</h3>
                    <p className="text-xs text-gray-600">Enable this to list the fully assembled product for sale.</p>
                 </div>
                 <Switch 
                   checked={formData.isSellingPhysicalProduct} 
                   onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isSellingPhysicalProduct: checked }))} 
                 />
              </div>

              <div className="md:col-span-2 flex items-center justify-between p-4 border border-gray-200 rounded-md bg-blue-50">
                 <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Sell Product Assembly Only</h3>
                    <p className="text-xs text-gray-600">If enabled, clicking to add/configure any individual component inside the 3D model viewer will instead automatically add this entire parent product assembly to the cart.</p>
                 </div>
                 <Switch 
                   checked={formData.sellProductOnly} 
                   onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sellProductOnly: checked }))} 
                 />
              </div>

              {formData.isSellingPhysicalProduct && (
                <>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Price *</label>
                    <Input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-7"
                    />
                    <span className="absolute left-3 top-[34px] text-gray-500 font-medium">$</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Limit (Optional)</label>
                    <Input
                      type="number"
                      name="purchaseLimit"
                      value={formData.purchaseLimit}
                      onChange={handleChange}
                      min="1"
                      placeholder="No limit"
                    />
                    <p className="text-[10px] text-gray-500 mt-0.5">Maximum units a customer can buy per order</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Stock Quantity *</label>
                    <Input
                      type="number"
                      name="stockQuantity"
                      value={formData.stockQuantity}
                      onChange={handleChange}
                      required
                      min="0"
                      placeholder="0"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {((product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles')) 
                        ? 'VIN *' 
                        : `Serial Number ${((product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles')) ? '*' : '(Optional)'}`
                      }
                    </label>
                    <Input
                      name="vinSn"
                      value={formData.vinSn}
                      onChange={handleChange}
                      onBlur={handleVinBlur}
                      error={vinError}
                      required={((product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles'))}
                      placeholder={((product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles')) ? 'Vehicle Identification Number' : 'Product Serial Number (Optional)'}
                    />
                    {((product?.categoryName || '')?.toLowerCase().includes('automotive') || (product?.categoryName || '')?.toLowerCase().includes('motorcycles')) && (
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center space-x-2 text-sm text-gray-600">
                          <input type="checkbox" checked={isPre1981} onChange={(e) => setIsPre1981(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500" />
                          <span>Is year before 1981?</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm text-gray-600">
                          <input type="checkbox" checked={isPre1969} onChange={(e) => setIsPre1969(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500" />
                          <span>Is year before 1969?</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition *</label>
                    <Select
                      name="condition"
                      value={formData.condition}
                      onChange={(val) => setFormData({ ...formData, condition: val })}
                      options={[
                        { value: 'New', label: 'New' },
                        { value: 'Used', label: 'Used' },
                        { value: 'Refurbished', label: 'Refurbished' },
                      ]}
                      required
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Stock Code (Optional)</label>
                <Input
                  type="text"
                  name="stockCode"
                  value={formData.stockCode}
                  onChange={handleChange}
                  placeholder="Enter your stock code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleStatusChange}
                  options={SELLER_PRODUCT_STATUSES}
                  placeholder="Select status"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (Optional)</label>
                <Input
                  type="text"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleChange}
                  placeholder="e.g. vintage, carbon, assembly"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seasonal Classification</label>
                <select
                  name="seasonalSetting"
                  value={formData.seasonalSetting}
                  onChange={(e) => setFormData(prev => ({ ...prev, seasonalSetting: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm h-10 bg-white"
                >
                  <option value="">None / All Seasons</option>
                  <option value="Spring">Spring</option>
                  <option value="Summer">Summer</option>
                  <option value="Fall">Fall</option>
                  <option value="Winter">Winter</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">US Holiday Theme</label>
                <select
                  name="holidaySetting"
                  value={formData.holidaySetting}
                  onChange={(e) => setFormData(prev => ({ ...prev, holidaySetting: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm h-10 bg-white"
                >
                  <option value="">None / Non-Holiday</option>
                  <option value="New Years">New Years</option>
                  <option value="Christmas">Christmas</option>
                  <option value="Easter">Easter</option>
                  <option value="Thanksgiving">Thanksgiving</option>
                  <option value="Halloween">Halloween</option>
                  <option value="4th of July">4th of July</option>
                  <option value="Presidents Day">Presidents Day</option>
                  <option value="Mothers Day">Mothers Day</option>
                  <option value="Fathers Day">Fathers Day</option>
                  <option value="Valentines">Valentines</option>
                  <option value="Labor Day">Labor Day</option>
                </select>
              </div>
              
              <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Product Badges</h3>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium w-32">Global Badges:</span>
                      {product?.isCertified && (
                          <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-md">FACTORY CERTIFIED</span>
                      )}
                      {product?.globalStaffpick && (
                          <span className="px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-md">GLOBAL STAFF PICK</span>
                      )}
                      {!product?.isCertified && !product?.globalStaffpick && (
                          <span className="text-sm text-gray-400 italic">No global badges assigned to this product</span>
                      )}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium w-32">Store Badges:</span>
                      <div className="flex items-center">
                        <Switch 
                          checked={formData.staffpick} 
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, staffpick: checked }))} 
                        />
                        <label className="ml-2 text-sm text-gray-700">Store Pick (Displays only in your store)</label>
                      </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Product Description (Optional)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your specific product description"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Product Images</label>
                <div className="mt-2">
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {imagePreviews.length > 0
                        ? `${imagePreviews.length} image(s) selected`
                        : 'Choose files (JPG, PNG, WEBP - Max 5MB)'}
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageFileSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
                {imagePreviews.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group aspect-square">
                        <img src={preview} alt={`Preview ${index}`} className="w-full h-full object-cover rounded-md" />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex flex-col items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                            title="Remove Image"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => makePrimaryImage(index)}
                            className={`opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-full p-1 shadow-md ${index === 0 ? 'bg-green-500' : 'bg-gray-500 hover:bg-green-500'}`}
                            title={index === 0 ? 'Primary Image' : 'Make Primary'}
                          >
                            <Star className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'manufacturing' && (
            <div className="md:col-span-2 space-y-6">
              {/* Manufacturer Routing Control */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-blue-600" />
                  Manufacturing & Order Routing
                </h3>
                <p className="text-xs text-gray-500">
                  Configure whether you self-manufacture (3D Print / CNC Machining) this item locally in your shop, or if orders should be automatically routed to an authorized digital manufacturing partner.
                </p>

                {globalMfgOpt?.offered_methods === 'none' && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs font-semibold leading-relaxed">
                    ⚠️ 3D Printing and CNC machining are not enabled globally for this product. Standard sellers cannot configure manufacturing configurations.
                  </div>
                )}

                <div className={`flex items-center justify-between p-4 border rounded-md bg-blue-50 mt-4 ${globalMfgOpt?.offered_methods === 'none' ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Self Manufacturing</h4>
                    <p className="text-xs text-gray-600">Enable this if you have the local equipment and material inventory to produce this part on-demand.</p>
                  </div>
                  <Switch 
                    checked={formData.isSelfManufacturing && globalMfgOpt?.offered_methods !== 'none'} 
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isSelfManufacturing: checked }))} 
                    disabled={globalMfgOpt?.offered_methods === 'none'}
                  />
                </div>

                {!formData.isSelfManufacturing && (
                  <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Select Fulfillment Partner (3D Print / CNC Account Holder)</label>
                    <select
                      name="manufacturerId"
                      value={formData.manufacturerId}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-900 font-medium"
                    >
                      <option value="">-- Choose An Authorized Manufacturer Account --</option>
                      {mfgAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                    {mfgAccounts.length === 0 && (
                      <p className="text-xs text-amber-600 mt-2">No active manufacturing service accounts registered in the database yet.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Only show 3D/CNC Capabilities if Self Manufacturing is enabled */}
              {formData.isSelfManufacturing && (
                <div className="space-y-6">
                  {/* 3D Printing Options */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Printer className="w-5 h-5 text-indigo-600" />
                        3D Print Configurations
                      </h3>
                      <Switch 
                        checked={formData.is3dManufacturing && isGlobal3dEnabled}
                        onCheckedChange={(val) => setFormData({ ...formData, is3dManufacturing: val })}
                        disabled={!isGlobal3dEnabled}
                      />
                    </div>

                    {!isGlobal3dEnabled && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3.5 rounded-lg text-xs font-medium leading-relaxed">
                        ⚠️ 3D Printing is not enabled globally for this product. If you believe this is in error, please contact site administration.
                      </div>
                    )}

                    {formData.is3dManufacturing && (
                      <div className="space-y-4 pt-2">
                        {/* Print types */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">3D Printer Technology Offered</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {allowed3dTypes.map(type => (
                              <label key={type} className="flex items-center gap-2 p-2 border rounded hover:bg-blue-50 cursor-pointer transition-colors text-sm">
                                <input
                                  type="checkbox"
                                  checked={formData.mfg3dTypes?.includes(type)}
                                  onChange={() => toggleArrayField('mfg3dTypes', type)}
                                  className="rounded text-blue-600 mr-2"
                                />
                                <span>{type}</span>
                              </label>
                            ))}
                            {allowed3dTypes.length === 0 && (
                              <p className="text-xs text-amber-600 col-span-full">No active 3D printing capabilities match the master product configurations.</p>
                            )}
                          </div>
                        </div>

                        {/* Standard Materials & Colors */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Standardized Materials & Colors</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                            {allowed3dMaterials.map(mat => {
                              const matName = typeof mat === 'string' ? mat : mat.name;
                              const isSelected = formData.mfg3dMaterials?.includes(matName);
                              return (
                                <label key={matName} className={`flex items-center gap-2 p-2 border rounded transition-all cursor-pointer text-sm ${isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-200'}`}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleArrayField('mfg3dMaterials', matName)}
                                    className="rounded text-blue-600 mr-2"
                                  />
                                  <span>{matName}</span>
                                </label>
                              );
                            })}
                            {allowed3dMaterials.length === 0 && (
                              <p className="text-xs text-amber-600 col-span-full">No active 3D materials match the master product configurations.</p>
                            )}
                          </div>

                          {/* Color select per selected material */}
                          {formData.mfg3dMaterials?.length > 0 && (
                            <div className="mt-4 p-4 bg-gray-50 border rounded-lg space-y-4">
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Configure Offered Colors per Material</label>
                              {formData.mfg3dMaterials.map(matName => {
                                const availableColors = [...new Set([
                                  ...(allowed3dColors[matName] || []),
                                  ...(formData.mfg3dColors?.[matName] || []),
                                  ...(customColorsRegistry[matName] || [])
                                ])];
                                return (
                                  <div key={matName} className="space-y-2 p-3 bg-white border rounded shadow-sm text-left">
                                    <span className="text-xs font-bold text-gray-800">{matName} Colors</span>
                                    <div className="flex flex-wrap gap-1.5">
                                      {availableColors.map(color => {
                                        const colorObj = parseColor(color);
                                        const isChecked = formData.mfg3dColors?.[matName]?.includes(color);
                                        let bgColor = colorObj.rgb;
                                        if (bgColor && !bgColor.startsWith('#') && bgColor.includes(',')) {
                                          bgColor = `rgb(${bgColor})`;
                                        }
                                        const isCustom = customColorsRegistry[matName]?.includes(color) || !allowed3dColors[matName]?.includes(color);
                                        return (
                                          <div key={`${matName}-${color}`} className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md cursor-pointer transition-all text-xs font-medium ${isChecked ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-gray-50 border-gray-200 hover:bg-indigo-50'}`} onClick={() => toggleColorForMaterial('mfg3dColors', matName, color)}>
                                            {colorObj.rgb && (
                                              <span 
                                                className="w-3.5 h-3.5 rounded-full border border-white/20 inline-block shadow-inner shrink-0" 
                                                style={{ backgroundColor: bgColor }}
                                              />
                                            )}
                                            <span>{colorObj.name}</span>
                                            {colorObj.isMetallic && <span className="text-[9px] opacity-80 uppercase font-bold px-1 bg-black/20 rounded">Metal</span>}
                                            {colorObj.isPolished && <span className="text-[9px] opacity-80 uppercase font-bold px-1 bg-black/20 rounded">Polish</span>}
                                            {isCustom && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation(); // Stop click from propagating to parent div click handler!
                                                  deleteCustomColor('mfg3dColors', matName, color);
                                                }}
                                                className="ml-1.5 p-0.5 rounded-full hover:bg-black/20 hover:text-white transition-colors flex items-center justify-center text-[10px]"
                                                title="Delete Custom Color"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {availableColors.length === 0 && (
                                        <p className="text-[10px] text-amber-600 italic">No matching colors available for this material.</p>
                                      )}
                                    </div>

                                    {/* Add Custom Color section */}
                                    <div className="border-t pt-2.5 mt-2 flex flex-col sm:flex-row gap-2 items-center">
                                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap">Add Custom:</span>
                                      <input
                                        type="text"
                                        placeholder="Color Name (e.g. Lime)"
                                        value={customColorName[matName] || ''}
                                        onChange={(e) => setCustomColorName({ ...customColorName, [matName]: e.target.value })}
                                        className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto flex-1 bg-white text-gray-900"
                                      />
                                      <input
                                        type="text"
                                        placeholder="RGB / Hex (Optional)"
                                        value={customColorRgb[matName] || ''}
                                        onChange={(e) => setCustomColorRgb({ ...customColorRgb, [matName]: e.target.value })}
                                        className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-28 bg-white text-gray-900"
                                      />
                                      {(() => {
                                        let rgbInput = (customColorRgb[matName] || '').trim();
                                        if (rgbInput) {
                                          if (!rgbInput.startsWith('#') && rgbInput.includes(',')) {
                                            rgbInput = `rgb(${rgbInput})`;
                                          }
                                          return (
                                            <span 
                                              className="w-5 h-5 rounded-full border border-gray-300 shadow-inner shrink-0 animate-in fade-in" 
                                              style={{ backgroundColor: rgbInput }}
                                              title="Live Swatch Preview"
                                            />
                                          );
                                        }
                                        return null;
                                      })()}
                                      <label className="flex items-center gap-1 text-[10px] font-black text-gray-500 cursor-pointer select-none">
                                        <input
                                          type="checkbox"
                                          checked={customColorMetallicChecked[matName] || false}
                                          onChange={(e) => setCustomColorMetallicChecked({ ...customColorMetallicChecked, [matName]: e.target.checked })}
                                          className="rounded text-indigo-600 w-3 h-3 border-gray-300"
                                        />
                                        <span>Metallic</span>
                                      </label>
                                      {customColorMetallicChecked[matName] && (
                                        <input
                                          type="number"
                                          min="0"
                                          max="1"
                                          step="0.1"
                                          placeholder="Value (0-1)"
                                          value={customColorMetallicValue[matName] ?? '1.0'}
                                          onChange={(e) => setCustomColorMetallicValue({ ...customColorMetallicValue, [matName]: e.target.value })}
                                          className="text-[10px] px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-16 bg-white text-gray-900"
                                        />
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const nameVal = customColorName[matName]?.trim();
                                          let rgbVal = customColorRgb[matName]?.trim() || '';
                                          if (!nameVal) return;

                                          let finalName = nameVal;
                                          if (!rgbVal) {
                                            if (!finalName.toLowerCase().endsWith('(natural)')) {
                                              finalName = `${finalName} (Natural)`;
                                            }
                                          }

                                          const isMetallic = !!customColorMetallicChecked[matName];
                                          const metallicVal = isMetallic ? (parseFloat(customColorMetallicValue[matName] ?? '1.0') || 1.0) : 0;
                                          const newColorObj = { name: finalName, rgb: rgbVal, isMetallic: isMetallic, metallic: metallicVal, isPolished: false };
                                          const colorStr = JSON.stringify(newColorObj);

                                          // Append to mfg3dColors directly and check it
                                          setFormData(prev => {
                                            const mfgColors = { ...prev.mfg3dColors };
                                            const currentList = mfgColors[matName] || [];
                                            if (!currentList.includes(colorStr)) {
                                              mfgColors[matName] = [...currentList, colorStr];
                                            }
                                            return { ...prev, mfg3dColors: mfgColors };
                                          });

                                          // Append to the transient select list
                                          setCustomColorsRegistry(prev => ({
                                            ...prev,
                                            [matName]: [...(prev[matName] || []), colorStr]
                                          }));

                                          setCustomColorName({ ...customColorName, [matName]: '' });
                                          setCustomColorRgb({ ...customColorRgb, [matName]: '' });
                                          setCustomColorMetallicChecked({ ...customColorMetallicChecked, [matName]: false });
                                          setCustomColorMetallicValue({ ...customColorMetallicValue, [matName]: '1.0' });
                                        }}
                                        disabled={!customColorName[matName]?.trim()}
                                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded text-[10px] font-bold disabled:opacity-50 h-[28px] flex items-center justify-center shrink-0"
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CNC Machining Options */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Cpu className="w-5 h-5 text-orange-600" />
                        CNC Machining Configurations
                      </h3>
                      <Switch 
                        checked={formData.isCncCapable && isGlobalCncEnabled}
                        onCheckedChange={(val) => setFormData({ ...formData, isCncCapable: val })}
                        disabled={!isGlobalCncEnabled}
                      />
                    </div>

                    {!isGlobalCncEnabled && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3.5 rounded-lg text-xs font-medium leading-relaxed">
                        ⚠️ CNC Machining is not enabled globally for this product. If you believe this is in error, please contact site administration.
                      </div>
                    )}

                    {formData.isCncCapable && (
                      <div className="space-y-4 pt-2">
                        {/* CNC Types */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">CNC Process Offered</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {allowedCncTypes.map(type => (
                              <label key={type} className="flex items-center gap-2 p-2 border rounded hover:bg-blue-50 cursor-pointer transition-colors text-sm">
                                <input
                                  type="checkbox"
                                  checked={formData.mfgCncTypes?.includes(type)}
                                  onChange={() => toggleArrayField('mfgCncTypes', type)}
                                  className="rounded text-blue-600 mr-2"
                                />
                                <span>{type}</span>
                              </label>
                            ))}
                            {allowedCncTypes.length === 0 && (
                              <p className="text-xs text-amber-600 col-span-full">No active CNC machining capabilities match the master product configurations.</p>
                            )}
                          </div>
                        </div>

                        {/* CNC Materials & Colors */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">CNC Machining Materials</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                            {allowedCncMaterials.map(mat => {
                              const matName = typeof mat === 'string' ? mat : mat.name;
                              const isSelected = formData.mfgCncMaterials?.includes(matName);
                              return (
                                <label key={matName} className={`flex items-center gap-2 p-2 border rounded transition-all cursor-pointer text-sm ${isSelected ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200 hover:border-orange-200'}`}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleArrayField('mfgCncMaterials', matName)}
                                    className="rounded text-blue-600 mr-2"
                                  />
                                  <span>{matName}</span>
                                </label>
                              );
                            })}
                            {allowedCncMaterials.length === 0 && (
                              <p className="text-xs text-amber-600 col-span-full">No active CNC materials match the master product configurations.</p>
                            )}
                          </div>

                          {/* CNC Grade select per selected material */}
                          {formData.mfgCncMaterials?.length > 0 && (
                            <div className="mt-4 p-4 bg-gray-50 border rounded-lg space-y-4">
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Configure Offered Grades/Finishes per Material</label>
                              {formData.mfgCncMaterials.map(matName => {
                                  const availableColors = [...new Set([
                                    ...(allowedCncColors[matName] || []),
                                    ...(formData.mfgCncColors?.[matName] || []),
                                    ...(customColorsRegistry[matName] || [])
                                  ])];
                                  return (
                                    <div key={matName} className="space-y-2 p-3 bg-white border rounded shadow-sm text-left">
                                      <span className="text-xs font-bold text-gray-800">{matName} Finishes</span>
                                      <div className="flex flex-wrap gap-1.5">
                                        {availableColors.map(color => {
                                          const colorObj = parseColor(color);
                                          const isChecked = formData.mfgCncColors?.[matName]?.includes(color);
                                          let bgColor = colorObj.rgb;
                                          if (bgColor && !bgColor.startsWith('#') && bgColor.includes(',')) {
                                            bgColor = `rgb(${bgColor})`;
                                          }
                                          const isCustom = customColorsRegistry[matName]?.includes(color) || !allowedCncColors[matName]?.includes(color);
                                          return (
                                            <div key={`${matName}-${color}`} className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md cursor-pointer transition-all text-xs font-medium ${isChecked ? 'bg-orange-600 border-orange-700 text-white' : 'bg-gray-50 border-gray-200 hover:bg-orange-50'}`} onClick={() => toggleColorForMaterial('mfgCncColors', matName, color)}>
                                              {colorObj.rgb && (
                                                <span 
                                                  className="w-3.5 h-3.5 rounded-full border border-white/20 inline-block shadow-inner shrink-0" 
                                                  style={{ backgroundColor: bgColor }}
                                                />
                                              )}
                                              <span>{colorObj.name}</span>
                                              {colorObj.isMetallic && <span className="text-[9px] opacity-80 uppercase font-bold px-1 bg-black/20 rounded">Metal</span>}
                                              {colorObj.isPolished && <span className="text-[9px] opacity-80 uppercase font-bold px-1 bg-black/20 rounded">Polish</span>}
                                              {isCustom && (
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation(); // Stop click from propagating to parent div click handler!
                                                    deleteCustomColor('mfgCncColors', matName, color);
                                                  }}
                                                  className="ml-1.5 p-0.5 rounded-full hover:bg-black/20 hover:text-white transition-colors flex items-center justify-center text-[10px]"
                                                  title="Delete Custom Finish"
                                                >
                                                  <X className="w-3 h-3" />
                                                </button>
                                              )}
                                            </div>
                                          );
                                          })}
                                          {availableColors.length === 0 && (
                                          <p className="text-[10px] text-amber-600 italic">No matching finishes available for this material.</p>
                                          )}
                                          </div>

                                          {/* Add Custom Finish section */}
                                          <div className="border-t pt-2.5 mt-2 flex flex-col sm:flex-row gap-2 items-center">
                                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap">Add Custom:</span>
                                          <input
                                          type="text"
                                          placeholder="Finish Name (e.g. Bead Blasted Red)"
                                          value={customColorName[matName] || ''}
                                          onChange={(e) => setCustomColorName({ ...customColorName, [matName]: e.target.value })}
                                          className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto flex-1 bg-white text-gray-900"
                                          />
                                          <input
                                          type="text"
                                          placeholder="RGB / Hex (Optional)"
                                          value={customColorRgb[matName] || ''}
                                          onChange={(e) => setCustomColorRgb({ ...customColorRgb, [matName]: e.target.value })}
                                          className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-28 bg-white text-gray-900"
                                          />
                                          {(() => {
                                          let rgbInput = (customColorRgb[matName] || '').trim();
                                          if (rgbInput) {
                                            if (!rgbInput.startsWith('#') && rgbInput.includes(',')) {
                                              rgbInput = `rgb(${rgbInput})`;
                                            }
                                            return (
                                              <span 
                                                className="w-5 h-5 rounded-full border border-gray-300 shadow-inner shrink-0 animate-in fade-in" 
                                                style={{ backgroundColor: rgbInput }}
                                                title="Live Swatch Preview"
                                              />
                                            );
                                          }
                                          return null;
                                          })()}
                                          <label className="flex items-center gap-1 text-[10px] font-black text-gray-500 cursor-pointer select-none">
                                            <input
                                              type="checkbox"
                                              checked={customColorMetallicChecked[matName] || false}
                                              onChange={(e) => setCustomColorMetallicChecked({ ...customColorMetallicChecked, [matName]: e.target.checked })}
                                              className="rounded text-orange-600 w-3 h-3 border-gray-300"
                                            />
                                            <span>Metallic</span>
                                          </label>
                                          {customColorMetallicChecked[matName] && (
                                            <input
                                              type="number"
                                              min="0"
                                              max="1"
                                              step="0.1"
                                              placeholder="Value (0-1)"
                                              value={customColorMetallicValue[matName] ?? '1.0'}
                                              onChange={(e) => setCustomColorMetallicValue({ ...customColorMetallicValue, [matName]: e.target.value })}
                                              className="text-[10px] px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-16 bg-white text-gray-900"
                                            />
                                          )}
                                          <button
                                          type="button"
                                          onClick={() => {
                                            const nameVal = customColorName[matName]?.trim();
                                            let rgbVal = customColorRgb[matName]?.trim() || '';
                                            if (!nameVal) return;

                                            let finalName = nameVal;
                                            if (!rgbVal) {
                                              if (!finalName.toLowerCase().endsWith('(natural)')) {
                                                finalName = `${finalName} (Natural)`;
                                              }
                                            }

                                            const isMetallic = !!customColorMetallicChecked[matName];
                                            const metallicVal = isMetallic ? (parseFloat(customColorMetallicValue[matName] ?? '1.0') || 1.0) : 0;
                                            const newColorObj = { name: finalName, rgb: rgbVal, isMetallic: isMetallic, metallic: metallicVal, isPolished: false };
                                            const colorStr = JSON.stringify(newColorObj);

                                            // Append to mfgCncColors directly and check it
                                            setFormData(prev => {
                                              const mfgColors = { ...prev.mfgCncColors };
                                              const currentList = mfgColors[matName] || [];
                                              if (!currentList.includes(colorStr)) {
                                                mfgColors[matName] = [...currentList, colorStr];
                                              }
                                              return { ...prev, mfgCncColors: mfgColors };
                                            });

                                            // Append to the transient select list
                                            setCustomColorsRegistry(prev => ({
                                              ...prev,
                                              [matName]: [...(prev[matName] || []), colorStr]
                                            }));

                                            setCustomColorName({ ...customColorName, [matName]: '' });
                                            setCustomColorRgb({ ...customColorRgb, [matName]: '' });
                                            setCustomColorMetallicChecked({ ...customColorMetallicChecked, [matName]: false });
                                            setCustomColorMetallicValue({ ...customColorMetallicValue, [matName]: '1.0' });
                                          }}
                                          disabled={!customColorName[matName]?.trim()}
                                          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded text-[10px] font-bold disabled:opacity-50 h-[28px] flex items-center justify-center shrink-0"
                                        >
                                          <Plus className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="md:col-span-2 bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm mt-4 text-left">
              {error}
            </div>
          )}

          <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={loading || uploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || uploading || !!vinError}>
              {uploading ? 'Uploading...' : loading ? 'Saving...' : 'Update Product'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}